import { AdMob, AdmobConsentStatus } from '@capacitor-community/admob'
import { Capacitor } from '@capacitor/core'
import ConfigService from '@/services/ConfigService'
import AppService from '@/services/AppService'
import StorageService from '@/services/StorageService'
import SubService from '@/services/SubService'

const ADD_OPTIONS = {
  adId: null,
  isTesting: false
}

let initilized = false

/**
 *
 * @param userValue
 * @param targetArray
 * @returns {*|boolean}
 */
function checkTargetCondition(userValue, targetArray) {
  if (!targetArray || targetArray.length === 0) {
    return true
  } else {
    return userValue && targetArray.includes(userValue)
  }
}

/**
 * Check if user should receive ads
 * @returns {Boolean}
 */
function isTargeted(targets) {
  const { country_code: country, device_platform: platform } = AppService.getAppInfo();
  const appInstanceId = AppService.getAppId();

  // lower case all values
  const isCountryTargeted = checkTargetCondition(country?.toLowerCase(), targets.countries?.map(c => c.toLowerCase()))
  const isPlatformTargeted = checkTargetCondition(platform?.toLowerCase(), targets.platforms?.map(p => p.toLowerCase()))
  const isAppInstanceTargeted = checkTargetCondition(appInstanceId, targets.app_instance_ids)

  return isCountryTargeted && isPlatformTargeted && isAppInstanceTargeted
}

export async function initializeAdMob () {
  if (initilized || SubService.isPro()) return

  const adUnitKey = Capacitor.getPlatform() === 'ios'
    ? 'admob_ad_unit_ios'
    : 'admob_ad_unit_android'

  const [enabled, adUnitId, targets] = await Promise.all([
    ConfigService.getBoolean('admob_enabled'),
    ConfigService.getString(adUnitKey),
    ConfigService.getJson('ads_targets')
  ])

  if (!enabled) {
    return
  }

  if (!isTargeted(targets)) {
    return
  }

  ADD_OPTIONS.adId = adUnitId

  await AdMob.initialize({
    testingDevices: [
      '03e1205ef9cec9a3ce7f1918e6ccd058', // Mikus iPhone
      'CFF4007A4FAB82659DD57B77CBEAC702' // Mikus Samsung
    ],
    initializeForTesting: true
  })

  try {
    const [
      trackingInfo,
      consentInfo
    ] = await Promise.all([
      AdMob.trackingAuthorizationStatus(),
      AdMob.requestConsentInfo(),
    ]);

    if (trackingInfo?.status === 'notDetermined') {
      /**
       * If you want to explain TrackingAuthorization before showing the iOS dialog,
       * you can show the modal here.
       * ex)
       * const modal = await this.modalCtrl.create({
       *   component: RequestTrackingPage,
       * });
       * await modal.present();
       * await modal.onDidDismiss();  // Wait for close modal
       **/
      await AdMob.requestTrackingAuthorization();
    }

    const authorizationStatus = await AdMob.trackingAuthorizationStatus();
    if (
      authorizationStatus.status === 'authorized' &&
      consentInfo.isConsentFormAvailable &&
      consentInfo.status === AdmobConsentStatus.REQUIRED
    ) {
      await AdMob.showConsentForm();
    }
  } catch (e) {
    console.error('AdMob.init: error getting tracking authorisation status or consent info', e)
  }

  // await AdMob.addListener(InterstitialAdPluginEvents.Loaded, (info) => console.log('----> InterstitialAdPluginEvents.Loaded', info))
  // await AdMob.addListener(InterstitialAdPluginEvents.FailedToLoad, (error) => console.log('----> InterstitialAdPluginEvents.FailedToLoad', error))
  // await AdMob.addListener(InterstitialAdPluginEvents.Showed, () => console.log('----> BannerAdPluginEvents.Showed'))
  // await AdMob.addListener(InterstitialAdPluginEvents.Dismissed, () => console.log('----> BannerAdPluginEvents.Dismissed'))

  initilized = true
}

export async function prepareInterstitial () {
  if (!initilized || SubService.isPro()) return

  return await AdMob.prepareInterstitial(ADD_OPTIONS)
    .catch(e => console.error('AdMob.prepareInterstitial prepareInterstitial: error', e))
}

export async function maybeShowInterstitial () {
  if (!initilized || SubService.isPro()) return

  // Check if last interstitial was shown more than 3min ago
  const admobInterval = await ConfigService.getNumber('admob_interval')
  const lastInterstitial = await StorageService.get('lastInterstitial', 0)
  const elapsed = Date.now() - lastInterstitial

  if (!lastInterstitial || elapsed > 1000 * admobInterval) {
    await AdMob.showInterstitial()
    await StorageService.set('lastInterstitial', Date.now())
  }
}

export async function showConsent() {
  const consentInfo = await AdMob.requestConsentInfo();

  if (consentInfo.isConsentFormAvailable && consentInfo.status === AdmobConsentStatus.REQUIRED) {
    const { status} = await AdMob.showConsentForm();
  }
}
